// apps/web/src/components/estoque/lote-combobox.tsx — Fase 13, Plano 14 (EST-09, D-22).
// Seletor de lote de cliente com saldo (Popover + lista, mesmo padrão de cliente-combobox.tsx). Consulta
// GET /api/v1/estoque/lookups/lotes (estoque:consultar; até 20 lotes com saldo positivo por lote, cliente ou código do material).
// Na contagem cega (D-24) o chamador passa `endpoint` do lookup cego do servidor: o item não traz saldo nem localização e o
// seletor não os exibe (CR-C01). O rótulo de saldo só aparece quando o servidor o enviou.
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import type { LoteLookupContagemEstoque, LoteLookupEstoque } from '@pieltec/contracts';
import { formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface LoteComboboxProps<T extends LoteLookupContagemEstoque = LoteLookupEstoque> {
  readonly value: T | null;
  readonly onChange: (lote: T) => void;
  /** Endpoint do lookup; o padrão traz saldo e localização (use o lookup cego na tela de contagem). */
  readonly endpoint?: string;
  readonly disabled?: boolean;
  readonly invalido?: boolean;
  readonly id?: string;
}

const ATRASO_BUSCA_MS = 250;
const ENDPOINT_LOTES_ESTOQUE = '/api/v1/estoque/lookups/lotes';

function temSaldo(lote: LoteLookupContagemEstoque): lote is LoteLookupEstoque {
  return 'saldo' in lote && 'localizacaoNome' in lote;
}

export function LoteCombobox<T extends LoteLookupContagemEstoque = LoteLookupEstoque>({
  value,
  onChange,
  endpoint = ENDPOINT_LOTES_ESTOQUE,
  disabled = false,
  invalido = false,
  id,
}: LoteComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [lotes, setLotes] = useState<readonly T[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const timer = window.setTimeout(() => {
      const termo = busca.trim();
      const query = termo ? `?busca=${encodeURIComponent(termo)}` : '';
      void fetch(`${endpoint}${query}`, { credentials: 'include' })
        .then(async (res) => {
          if (!ativo) return;
          if (!res.ok) {
            setErro('Não foi possível carregar os lotes.');
            setLotes([]);
            return;
          }
          setLotes((await res.json()) as T[]);
        })
        .catch(() => {
          if (ativo) setErro('Não foi possível carregar os lotes.');
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
    }, ATRASO_BUSCA_MS);
    return () => {
      ativo = false;
      window.clearTimeout(timer);
    };
  }, [open, busca, endpoint]);

  return (
    <Popover
      open={open}
      onOpenChange={(proximo) => {
        setOpen(proximo);
        if (!proximo) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalido || undefined}
          className={`ui-control w-full flex items-center justify-between min-h-[var(--control-height,2rem)] px-3 py-2 bg-white border rounded-md text-sm text-slate-800 disabled:opacity-50 ${
            invalido ? 'border-rose-500' : 'border-slate-300'
          }`}
        >
          <span className="truncate">{value ? `${value.loteInternoPieltec} — ${value.clienteNome}` : 'Selecione o lote...'}</span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" aria-hidden="true" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 overflow-hidden"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          buscaRef.current?.focus();
        }}
      >
        <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
          <input
            ref={buscaRef}
            type="text"
            aria-label="Buscar lote"
            placeholder="Buscar por lote, cliente ou código"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent text-sm text-slate-900 focus:outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox" aria-label="Lotes">
          {carregando ? (
            <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Carregando lotes...
            </div>
          ) : erro ? (
            <div role="alert" className="p-4 text-center text-sm text-rose-700">
              {erro}
            </div>
          ) : lotes.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">Nenhum lote encontrado.</div>
          ) : (
            lotes.map((lote) => {
              const selecionado = value?.id === lote.id;
              return (
                <button
                  key={lote.id}
                  type="button"
                  role="option"
                  aria-selected={selecionado}
                  onClick={() => {
                    onChange(lote);
                    setOpen(false);
                    setBusca('');
                  }}
                  className={`w-full min-h-[var(--control-height,2rem)] flex items-center justify-between px-3 py-2 text-left text-sm rounded-md transition-colors ${
                    selecionado ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex flex-col truncate pr-2">
                    <span className="truncate">
                      {lote.loteInternoPieltec} — {lote.clienteNome}
                    </span>
                    <span className={`text-xs ${selecionado ? 'text-slate-300' : 'text-slate-500'}`}>
                      {temSaldo(lote)
                        ? `${lote.materialCodigo} · saldo ${formatarQuantidadeEstoque(lote.saldo, '')} · ${lote.localizacaoNome}`
                        : lote.materialCodigo}
                    </span>
                  </span>
                  {selecionado && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
